<script lang="ts" setup>
import { computed, onBeforeUnmount, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'

import { useResponsive } from '@/composables/useResponsive'
import { useMenuChildrenStore } from '@/stores/menuChildrenStore'
import { useMenuStore } from '@/stores/menuStore'
import { useSidebarStore } from '@/stores/sidebar.store'
import { IMenuItem } from '@/types/menu.type'

const route = useRoute()
const router = useRouter()
const menuStore = useMenuStore()
const menuChildrenStore = useMenuChildrenStore()
const sidebarStore = useSidebarStore()
const { isMobile } = useResponsive()

const menuItems = computed(() => menuStore.getItems)

let timeout: ReturnType<typeof setTimeout> | null = null

const clearTimer = () => {
  if (timeout) {
    clearTimeout(timeout)
    timeout = null
  }
}

const setMenuChildren = (parent: string, items: IMenuItem[]) => {
  clearTimer()

  timeout = setTimeout(() => {
    menuChildrenStore.updateMenuStore(parent, items)
  }, 250)
}

const mouseEnterHandler = (item: IMenuItem) => {
  if (isMobile.value || menuChildrenStore.getIsLocked) {
    return
  }

  setMenuChildren(item.title, item.children)
}

const mouseLeaveHandler = () => {
  if (isMobile.value || menuChildrenStore.getIsLocked) {
    return
  }

  clearTimer()
}

const onClick = async (item: IMenuItem) => {
  if (item.children?.length) {
    const isCurrentItem = menuChildrenStore.getParentTitle === item.title &&
      menuChildrenStore.getChildItemsCount > 0

    if (isCurrentItem && menuChildrenStore.getIsLocked) {
      menuChildrenStore.setIsLocked(false)
      menuChildrenStore.updateMenuStore()
      return
    }

    menuChildrenStore.updateMenuStore(item.title, item.children)
    menuChildrenStore.setIsLocked(true)
    return
  }

  menuChildrenStore.updateMenuStore()
  menuChildrenStore.setIsLocked(false)
  await router.push(item.url)

  if (isMobile.value) {
    sidebarStore.close()
  }
}

const hasActiveItem = (item: IMenuItem): boolean => {
  return item.active || (item.children || []).some(hasActiveItem)
}

const isActiveMenuItem = (item: IMenuItem) => {
  return hasActiveItem(item)
}

const isExpandedMenuItem = (item: IMenuItem) => {
  return menuChildrenStore.getParentTitle === item.title &&
    menuChildrenStore.getChildItemsCount > 0
}

watch(route, () => {
  menuStore.setActiveItemFromRoute(route)
})

onBeforeUnmount(clearTimer)
</script>

<template>
  <nav
    class="menu-items"
    aria-label="Разделы"
    @mouseleave="mouseLeaveHandler"
  >
    <component
      :is="menuItem.children?.length ? 'button' : RouterLink"
      v-for="menuItem in menuItems"
      :key="menuItem.title"
      :to="menuItem.children?.length ? undefined : menuItem.url"
      :aria-expanded="menuItem.children?.length ? isExpandedMenuItem(menuItem) : undefined"
      :aria-controls="menuItem.children?.length ? 'sidebar-submenu' : undefined"
      :aria-current="!menuItem.children?.length && isActiveMenuItem(menuItem) ? 'page' : undefined"
      :class="[
        isActiveMenuItem(menuItem) ? 'active-route' : '',
        isExpandedMenuItem(menuItem) ? 'expanded' : '',
        'menu-item cursor-pointer'
      ]"
      @mouseenter="mouseEnterHandler(menuItem)"
      @focus="mouseEnterHandler(menuItem)"
      @click="onClick(menuItem)"
    >
      <span class="menu-item__content">
        <VIcon
          v-if="menuItem.vIcon"
          :icon="menuItem.vIcon"
        />
        <span
          v-else
          class="material-symbols"
        >
          {{ menuItem.icon }}
        </span>
        <span class="menu-item__title">{{ menuItem.title }}</span>
        <VIcon
          v-if="menuItem.children?.length"
          class="menu-item__chevron"
          icon="mdi-chevron-right"
          size="18"
        />
      </span>
    </component>
  </nav>
</template>

<style lang="scss">
.menu-items {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: var(--ui-menu-text);

  .menu-item {
    display: block;
    box-sizing: border-box;
    width: 100%;
    min-height: 42px;
    padding: 0;
    color: inherit;
    background: transparent;
    border: 0;
    border-radius: var(--ui-radius-sm);
    font: inherit;
    text-align: left;
    text-transform: none;
    transition: background-color 0.18s ease, color 0.18s ease;

    &:hover {
      color: var(--ui-menu-hover-text);
      background: var(--ui-menu-hover-background);
    }

    &:focus-visible {
      color: var(--ui-menu-hover-text);
      background: var(--ui-menu-hover-background);
      outline: 2px solid var(--ui-menu-hover-text);
      outline-offset: -2px;
    }
  }

  .menu-item__content {
    display: flex;
    align-items: center;
    min-height: inherit;
    padding: 8px 10px;
    gap: 10px;
    line-height: 1.2;
  }

  .menu-item__title {
    flex: 1 1 auto;
  }

  .menu-item__chevron {
    flex: 0 0 auto;
    transition: transform 0.18s ease;
  }

  .menu-item.expanded .menu-item__chevron {
    transform: rotate(90deg);
  }
}

.menu-item.active-route,
.menu-item.router-link-active {
  background: var(--ui-menu-active-background);
  box-shadow: inset 3px 0 0 var(--ui-menu-hover-text);
}
</style>
